import type { ExecuteActionResponse } from "@ando/core";
import type { ActionFieldValue } from "@ando/ui";
import * as React from "react";

import { useActiveActionStore } from "../state/activeActionStore";
import { isProposalExpired } from "../utils/isProposalExpired";
import { activeActionApi } from "./useActiveAction";
import { useExecuteAction } from "./useExecuteAction";

/**
 * useProposalExecution — the ONE shared HITL execute lifecycle.
 *
 * Every proposal surface (``FocusedActionView``, ``ScalarActionProposalMount``,
 * ``SlideDeckProposalMount``) committed an action through the SAME three-beat
 * dance, copy-pasted in each file:
 *
 *   1. ``activeActionApi.patch(id, { status: "executing", ...editedFields })``
 *   2. ``executeMutation.mutate({ proposalId, modifications }, { onSuccess, onError })``
 *   3a. onSuccess → ``patch(id, { status: "executed", executedData? })``
 *   3b. onError   → ``patch(id, { status: "proposed", error })``
 *
 * The optimistic ``executing`` flip + the authoritative ``executed`` /
 * rollback-to-``proposed`` patches are pure boilerplate — identical across
 * all three sites (see ``useExecuteAction`` docstring for WHY the HTTP
 * response is authoritative rather than waiting on a WS frame). This hook
 * owns exactly that boilerplate and nothing else.
 *
 * Site-specific work stays at the call site via the ``onSuccess`` /
 * ``onError`` / ``onSettled`` callback seams:
 *   - ``SlideDeckProposalMount``: cache invalidation + "Deck aggiornato"
 *     toast + in-place refresh (``onSuccess``); deck-specific error toast.
 *   - ``ScalarActionProposalMount`` / ``FocusedActionView``: per-site error
 *     toast; ``FocusedActionView``'s ``proposalsStore`` decision rollback.
 *
 * The hook deliberately does NOT toast — the copy differs per site
 * ("Generazione del deck fallita" vs "Esecuzione azione fallita") so the
 * message belongs with the caller, inside its ``onError`` seam.
 */

export interface ProposalExecuteOptions {
  /**
   * Field map to stash on the action as ``editedFields`` alongside the
   * optimistic ``status: "executing"`` flip. ``FocusedActionView`` and
   * ``ScalarActionProposalMount`` persist the operator's edits here so a
   * reload / re-open re-hydrates them; ``SlideDeckProposalMount`` omits it
   * (its outline lives in the slide draft, not ``editedFields``).
   */
  readonly editedFields?: Readonly<Record<string, ActionFieldValue>>;
  /**
   * Called AFTER the authoritative ``status: "executed"`` (+ ``executedData``)
   * patch lands. Site-specific success work (cache invalidation, success
   * toast, in-place refresh) goes here.
   */
  readonly onSuccess?: (res: ExecuteActionResponse) => void;
  /**
   * Called AFTER the rollback ``status: "proposed"`` (+ ``error``) patch
   * lands. Site-specific error work (error toast, decision rollback) goes
   * here.
   */
  readonly onError?: (err: Error) => void;
  /** Called after either branch settles — mirrors TanStack ``onSettled``. */
  readonly onSettled?: () => void;
}

export interface UseProposalExecutionResult {
  /**
   * Run the shared execute lifecycle for ``proposalId``. ``modifications``
   * is the post-diff field map (or ``undefined`` for no changes) — exactly
   * what ``useExecuteAction`` expects.
   */
  readonly execute: (
    proposalId: string,
    modifications: Readonly<Record<string, unknown>> | undefined,
    opts?: ProposalExecuteOptions,
  ) => void;
  /** ``true`` while the execute mutation is in flight (drives Conferma disabling). */
  readonly busy: boolean;
}

export function useProposalExecution(): UseProposalExecutionResult {
  const executeMutation = useExecuteAction();

  const execute = React.useCallback(
    (
      proposalId: string,
      modifications: Readonly<Record<string, unknown>> | undefined,
      opts: ProposalExecuteOptions = {},
    ) => {
      // EXPIRY GATE (Jul 1, 2026) — single chokepoint for ALL accept surfaces
      // (chip → drawer Conferma → editor redline "Accetta tutte"). If the
      // server-side proposal TTL has lapsed, the BE would reject with 502
      // "Proposta scaduta"; short-circuit HERE with a clear, actionable message
      // and NO network call, so a stale proposal can't be clicked into an error.
      const _existing = useActiveActionStore.getState().actions[proposalId];
      if (isProposalExpired(_existing?.expiresAt)) {
        activeActionApi.patch(proposalId, {
          status: "proposed",
          error: "Questa proposta è scaduta. Rilancia la richiesta per rigenerarla.",
        });
        opts.onSettled?.();
        return;
      }
      // Clear any prior failure classification so a RETRY reads as a clean
      // attempt (the inline recovery banner is keyed off ``errorKind``).
      activeActionApi.patch(proposalId, {
        status: "executing",
        error: null,
        errorKind: null,
        errorMeta: null,
        ...(opts.editedFields ? { editedFields: opts.editedFields } : {}),
      });
      executeMutation.mutate(
        { proposalId, modifications },
        {
          onSuccess: (res) => {
            // ASYNC dispatch (document_create on Temporal): the BE returns a
            // FAST ``{status:"executing"}`` ack — the real completion (+ the doc
            // coordinates for auto-open) arrives later via the
            // ``action_status: completed`` WS frame (handled in useActionRouter).
            // Keep the status ``executing`` so the blob loader stays, and do NOT
            // run the HTTP-path ``onSuccess`` (which would try to open with no
            // doc_id) — the WS path owns the open.
            const isAsyncPending =
              (res?.result_data as { status?: unknown } | undefined)?.status === "executing";
            if (isAsyncPending) {
              activeActionApi.patch(proposalId, { status: "executing" });
              opts.onSettled?.();
              return;
            }
            // CLASSIFIED FAILURE (Jul 2026): the sync executor path returns
            // ``success:false`` as an HTTP 200 body (no ``action_status: failed``
            // WS frame is emitted for it — the HTTP response is authoritative).
            // Map it to an ``error`` state carrying the machine-readable
            // ``error_kind`` so the card renders inline recovery instead of a
            // generic toast. Never flip a failed action to ``executed``.
            if (res && res.success === false) {
              activeActionApi.patch(proposalId, {
                status: "error",
                error: res.message,
                errorKind: res.error_kind ?? "generic",
                errorMeta: res.error_meta ?? null,
              });
              const failErr = new Error(res.message) as Error & {
                kind?: string;
              };
              failErr.kind = res.error_kind ?? "generic";
              opts.onError?.(failErr);
              opts.onSettled?.();
              return;
            }
            // SOT: the HTTP response is authoritative for completion (the BE
            // doesn't emit an ``action_status: executed`` WS frame for every
            // success branch). Stash ``result_data`` + the normalised RECEIPT
            // (result_url / result_label / external_id) so the executed card
            // (and chat-history chips) can deep-link to the materialised artefact.
            const executedData: Record<string, unknown> = {
              ...(res?.result_data ?? {}),
              ...(res?.result_url != null ? { result_url: res.result_url } : {}),
              ...(res?.result_label != null ? { result_label: res.result_label } : {}),
              ...(res?.external_id != null ? { external_id: res.external_id } : {}),
            };
            activeActionApi.patch(proposalId, {
              status: "executed",
              error: null,
              errorKind: null,
              errorMeta: null,
              ...(Object.keys(executedData).length > 0 ? { executedData } : {}),
            });
            opts.onSuccess?.(res);
            opts.onSettled?.();
          },
          onError: (err) => {
            // The REST call itself failed (network drop / abort / non-2xx). No
            // structured ``error_kind`` from the BE here — treat as transient so
            // the inline recovery offers a Retry. ``status: "proposed"`` keeps
            // the decision bar available too (the banner is keyed off errorKind).
            activeActionApi.patch(proposalId, {
              status: "proposed",
              error: err.message,
              errorKind: "transient",
              errorMeta: { network: true },
            });
            const netErr = err as Error & { kind?: string };
            netErr.kind = "transient";
            opts.onError?.(netErr);
            opts.onSettled?.();
          },
        },
      );
    },
    [executeMutation],
  );

  return { execute, busy: executeMutation.isPending };
}
